import { useEffect, useMemo } from 'react';
import { Chess } from 'chess.js';
import { proxy, useSnapshot } from 'valtio';

import { type MoveInfo } from '@exports/ChessGame/ChessGame';
import { UciEngine } from '@exports/Valuator';
import { mapWinner } from '@libs/chess/movesToFen';
import { parseFen } from '@libs/chess/parseFen';
import { PromotionPiece, SquareName } from '@libs/chess/types';
import { delay } from '@libs/delay';

// FIXME: не регистрируется ничья из-за повторяющихся ходов (в snapshot, в props — регистрируется)

/*
    ____,-------------------------------,____
    \   |              Hook             |   /
    /___|-------------------------------|___\
*/

export type GameConfig = {
  id: string;
  fen: string;
  elo: number;
  color: 'white' | 'black';
  white?: GameConfigTime;
  black?: GameConfigTime;
};

export type GameConfigTime = {
  timeInitialSec: number;
  timeAddSec: number;
};

type MoveMinimal = {
  san: string;
  from: SquareName;
  to: SquareName;
  fenAfter: string;
  clockAfter?: {
    whiteLeftMs: number;
    blackLeftMs: number;
  };
};

export function useComputerGame({
  cfg,
  disabled = false,
}: {
  cfg: GameConfig;
  disabled?: boolean;
}) {
  const store = useMemo(() => {
    const s = new ComputerGameStore(cfg);
    s.start(disabled);
    return s;
  }, [cfg, disabled]);
  useEffect(() => () => store.destroy(), [store]);

  const snapshot = useSnapshot(store.state);

  return { snapshot, store };
}

/*
    ____,-------------------------------,____
    \   |             State             |   /
    /___|-------------------------------|___\
*/

class ComputerGameState {
  fen = '';
  turn: 'white' | 'black' = 'white';
  moves: MoveMinimal[] = [];
  lastMove: MoveInfo | null = null;
  premove: MoveInfo | null = null;
  state: 'initializing' | 'idle' | 'thinking' | 'finished' = 'initializing';
  winner?: 'white' | 'black' | 'draw';
  timestampMs = 0;
  whiteClockMs?: number;
  blackClockMs?: number;
}

/*
    ____,-------------------------------,____
    \   |             Store             |   /
    /___|-------------------------------|___\
*/

class ComputerGameStore {
  state: ComputerGameState;

  private chess: Chess;
  private engine: UciEngine;
  private playerColor: 'w' | 'b';
  private computerColor: 'w' | 'b';
  private clockTimeout?: ReturnType<typeof setTimeout>;

  constructor(private cfg: GameConfig) {
    this.chess = new Chess(cfg.fen, { enable960: true });
    this.engine = UciEngine.createLite(mapEloToSKill(cfg.elo));

    this.playerColor = cfg.color === 'white' ? 'w' : 'b';
    this.computerColor = cfg.color === 'white' ? 'b' : 'w';

    this.state = proxy(new ComputerGameState());
    this.state.fen = cfg.fen;
    this.state.turn = this.getInitialTurn(cfg.fen);
    this.state.timestampMs = Date.now();
    this.state.whiteClockMs = cfg.white ? cfg.white.timeInitialSec * 1000 : undefined;
    this.state.blackClockMs = cfg.black ? cfg.black.timeInitialSec * 1000 : undefined;
    this.state.state = this.engine.status === 'initializing' ? 'initializing' : 'idle';
  }

  // ---- Lifecycle ----

  start(disabled: boolean) {
    const init = () => {
      this.state.state = 'idle';
      this.state.timestampMs = Date.now();
      if (!disabled) {
        this.watchClock(this.state.turn);
      }

      if (this.state.turn.startsWith(this.computerColor) && !disabled) {
        this.makeComputerMove(this.cfg.fen);
      }
    };

    if (this.engine.status !== 'initializing') {
      init();
    } else {
      const u = this.engine.subscribe((e) => {
        if (e.type === 'initialized' && this.state.state === 'initializing') {
          init();
          u();
        }
      });
    }
  }

  destroy = () => {
    this.state.state = 'finished';
    this.engine.terminate();
  };

  // ---- Player actions ----

  onPlayerMove = (m: MoveInfo) => {
    if (this.state.state === 'finished') return;

    const playerTime = Date.now();

    this.chess.move(m);
    this.state.fen = this.chess.fen();
    this.state.turn = this.getColor(this.chess);
    this.state.lastMove = { from: m.from, to: m.to, promotion: m.promotion };
    this.updateClock(this.playerColor, playerTime - this.state.timestampMs);
    this.state.timestampMs = playerTime;
    const prevMove = this.getMinimalMove(
      this.chess,
      this.state.whiteClockMs,
      this.state.blackClockMs,
    );
    this.state.moves.push(prevMove);

    if (this.chess.isGameOver()) {
      this.state.winner = mapWinner(this.chess);
      this.destroy();
      return;
    }

    this.watchClock(this.state.turn);
    this.makeComputerMove(this.chess.fen());
  };

  onPlayerPremove = (m: MoveInfo | null) => {
    this.state.premove = m;
  };

  onPlayerSurrender = () => {
    if (this.state.state === 'finished') return;

    this.state.winner = this.playerColor === 'w' ? 'black' : 'white';
    this.state.state = 'finished';

    const time = Date.now();
    this.updateClock(this.state.turn === 'white' ? 'w' : 'b', time - this.state.timestampMs, false);
    this.state.timestampMs = time;
  };

  // ---- Private ----

  private async onComputerMove(m: string) {
    const elapsed = Date.now() - this.state.timestampMs;
    if (elapsed < 1000) {
      await delay(1000 - elapsed);

      if (this.state.state === 'finished') {
        return;
      }
    }

    this.chess.move(m);
    this.state.fen = this.chess.fen();
    this.state.turn = this.getColor(this.chess);
    const computerTime = Date.now();
    this.updateClock(this.computerColor, computerTime - this.state.timestampMs);
    this.state.timestampMs = computerTime;
    const curMove = this.getMinimalMove(
      this.chess,
      this.state.whiteClockMs,
      this.state.blackClockMs,
    );
    this.state.moves.push(curMove);

    this.state.lastMove = {
      from: curMove.from,
      to: curMove.to,
      promotion: m[4] as PromotionPiece | undefined,
    };

    if (this.chess.isGameOver()) {
      this.state.winner = mapWinner(this.chess);
      this.destroy();
      return;
    }

    this.watchClock(this.state.turn);

    if (this.state.premove) {
      const premove = this.state.premove;

      try {
        const tmpChess = new Chess(this.chess.fen(), { enable960: true });
        tmpChess.move(premove);

        setTimeout(() => {
          if (this.state.state === 'finished') return;
          this.state.premove = null;
          this.onPlayerMove(premove);
        }, 0);
      } catch {
        this.state.premove = null;
      }
    }
  }

  private makeComputerMove(fenBefore: string) {
    if (this.cfg.elo === 0) {
      const allMoves = this.chess.moves();
      const m = allMoves[Math.floor(Math.random() * allMoves.length)]!;

      this.onComputerMove(m);
      return;
    }

    this.engine.evaluate({
      fen: fenBefore,
      movetime: 1000,
    });
    this.state.state = 'thinking';

    const unsubscribe = this.engine.subscribe(async (v) => {
      if (v.type === 'interrupted' || this.state.state === 'finished') {
        unsubscribe();
        return;
      }

      if (v.type === 'finished') {
        this.state.state = 'idle';
        unsubscribe();

        this.onComputerMove(v.bestmove);
      }
    });
  }

  private updateClock(color: 'b' | 'w', elapsedMs: number, allowAdd = true) {
    const isWhite = color === 'w';
    const clock = isWhite ? 'whiteClockMs' : 'blackClockMs';
    const setting = isWhite ? 'white' : 'black';

    const time = this.state[clock];
    const add = allowAdd ? (this.cfg[setting]?.timeAddSec ?? 0) * 1000 : 0;

    if (time !== undefined) {
      this.state[clock] = time - elapsedMs + add;
    }
  }

  private watchClock(color: 'black' | 'white') {
    if (this.clockTimeout) {
      clearTimeout(this.clockTimeout);
      this.clockTimeout = undefined;
    }

    if (this.state.state === 'finished') return;

    const isWhite = color === 'white';
    const time = this.state[isWhite ? 'whiteClockMs' : 'blackClockMs'];

    if (!time) return;

    this.clockTimeout = setTimeout(() => {
      if (this.state.state === 'finished') return;

      this.state[isWhite ? 'whiteClockMs' : 'blackClockMs'] = 0;
      this.state.winner = isWhite ? 'black' : 'white';
      this.state.state = 'finished';
      this.clockTimeout = undefined;
    }, time);
  }

  private getInitialTurn(fen: string): ComputerGameState['turn'] {
    const f = parseFen(fen);
    if (!f) {
      throw new Error(`Inalid FEN ${fen}`);
    }

    return f.color;
  }

  private getMinimalMove(chess: Chess, whiteClock?: number, blackClock?: number): MoveMinimal {
    const h = chess.history({ verbose: true });
    const l = h[h.length - 1]!;

    return {
      fenAfter: chess.fen(),
      from: l.from,
      to: l.to,
      san: l.san,
      clockAfter:
        whiteClock && blackClock ? { whiteLeftMs: whiteClock, blackLeftMs: blackClock } : undefined,
    };
  }

  private getColor(chess: Chess): 'white' | 'black' {
    return chess.turn() === 'w' ? 'white' : 'black';
  }
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

function mapEloToSKill(elo: number) {
  if (elo >= 2500) return 20;
  if (elo >= 2400) return 18;
  if (elo >= 2300) return 15;
  if (elo >= 2200) return 14;
  if (elo >= 2100) return 13;
  if (elo <= 2000 && elo >= 1000) {
    // 2000 — 10
    // 1900 — 9
    // ...
    // 1000 — 1
    return Math.floor(elo / 100 - 10);
  }
  if (elo >= 300) return 0;
}
